import { useCallback, useEffect, useState } from "react";
import { useContextSite } from "../../../context/Context";
import { Gerenciamento } from "../../../services/Gerenciamento";
import { toast } from "react-toastify";
import { GraphColors } from "../../../utils/graphCorlors";
import { ITendenciaDTO } from "../../../types/tendencia";
import { maskMoney } from "../../../utils/masks";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { MakeOptional } from "@mui/x-charts/models/helpers";
import { LineSeriesType } from "@mui/x-charts";
import { Capitalize } from "../../../utils/capitalize";

// Paleta fixa para as unidades (cicla a partir da 9ª)
const UNIT_COLORS = [
  "#1D1D1B",
  "#E84E1B",
  "#266BF0",
  "#2F9E44",
  "#7C4DCC",
  "#B83A12",
  "#0F766E",
  "#8A8A84",
];

const shortDate = (d: string) => dayjs(d.slice(0, 10)).format("DD/MM");

export interface ILineChartprops {
  xAxis: string[];
  series: MakeOptional<LineSeriesType, "type">[];
}

export const useDataTendencia = () => {
  const { empresa } = useParams();
  const { setIsLoad } = useContextSite();

  const [data, setData] = useState<ITendenciaDTO>({} as ITendenciaDTO);
  const [tendenciasGeral, setTendenciasGeral] = useState<ILineChartprops>(
    {} as ILineChartprops
  );

  const [tendenciasTipoServico, setTendenciasTipoServicos] =
    useState<ILineChartprops>({} as ILineChartprops);

  const [tendenciasUnidade, setTendenciasUnidade] = useState<ILineChartprops>(
    {} as ILineChartprops
  );
  const [tendenciasAtendimento, setTendenciasAtendimento] =
    useState<ILineChartprops>({} as ILineChartprops);

  const getTendencias = useCallback(() => {
    setIsLoad(true);

    function formatDataTendenciasGeral(data: ITendenciaDTO): ILineChartprops {
      const formattedXAxis = data.geral.map((e) => shortDate(e.data));

      const formattedSeries = [
        {
          data: data.geral.map((e) => e.qtdTotal),
          label: "Total",
          color: GraphColors[Capitalize(data?.empresa)],
          valueFormatter: (value: number) => value?.toString(),
        },
      ];

      return {
        xAxis: formattedXAxis,
        series: formattedSeries,
      };
    }

    function formatDataTendenciasServicos(
      data: ITendenciaDTO
    ): ILineChartprops {
      const formattedXAxis = data.geral.map((e) => shortDate(e.data));

      const formattedSeries = [
        {
          data: data.geral.map((e) => e.qtdPrimeiroEmplacamento),
          label: "Primeiro Emplacamento",
          color: "#E84E1B",
        },
        {
          data: data.geral.map((e) => e.qtdTransferencia),
          color: "#3C3C3B",
          label: "Transferência",
        },
      ];

      return {
        xAxis: formattedXAxis,
        series: formattedSeries,
      };
    }

    function formatDataTendenciasAtendimento(
      data: ITendenciaDTO
    ): ILineChartprops {
      const formattedXAxis = data.geral.map((e) => shortDate(e.data));

      const formattedSeries = [
        {
          data: data.geral.map((e) => e.qtdLoja),
          label: "Loja",
          color: "#1D1D1B",
        },
        {
          data: data.geral.map((e) => e.qtdDelivery),
          color: "#E84E1B",
          label: "Delivery",
        },
      ];

      return {
        xAxis: formattedXAxis,
        series: formattedSeries,
      };
    }

    function formatDataTendenciasUnidade(data: ITendenciaDTO): ILineChartprops {
      // Datas ISO ordenam como string; cada unidade é alinhada pelo dia
      const dates = Array.from(
        new Set(data.unidades.flatMap((u) => u.dias.map((d) => d.data)))
      ).sort();
      const formattedXAxis = dates.map(shortDate);

      const formattedSeries = data.unidades.map((unidade, i) => {
        const porData = new Map(unidade.dias.map((d) => [d.data, d.qtdTotal]));
        return {
          data: dates.map((d) => porData.get(d) ?? 0),
          label: unidade.cidade,
          color: UNIT_COLORS[i % UNIT_COLORS.length],
        };
      });

      return {
        xAxis: formattedXAxis,
        series: formattedSeries,
      };
    }

    Gerenciamento.tendencia({ empresa })
      .then(({ data }) => {
        if (data?.length > 0) {
          setData(data[0]);

          const geral = formatDataTendenciasGeral(data[0]);
          const servicos = formatDataTendenciasServicos(data[0]);
          const atendimento = formatDataTendenciasAtendimento(data[0]);
          const unidade = formatDataTendenciasUnidade(data[0]);

          setTendenciasGeral(geral);
          setTendenciasTipoServicos(servicos);
          setTendenciasAtendimento(atendimento);
          setTendenciasUnidade(unidade);

          return;
        }

        setTendenciasGeral({} as ILineChartprops);
        setTendenciasTipoServicos({} as ILineChartprops);
        setTendenciasAtendimento({} as ILineChartprops);
        setTendenciasUnidade({} as ILineChartprops);
        setData({} as ITendenciaDTO);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => {
        setIsLoad(false);
      });
  }, [empresa]);

  useEffect(() => {
    if (empresa) {
      getTendencias();
    }
  }, [getTendencias, empresa]);

  return {
    data,
    tendenciasGeral,
    tendenciasTipoServico,
    tendenciasAtendimento,
    tendenciasUnidade,
  };
};
